import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const CARD_SX = {
    p: 3,
    borderRadius: '16px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 4px 20px rgba(17,24,39,0.05)',
};

const BiometricIntegrationGuide = () => {
    return (
        <Box sx={{ mt: 2 }}>
            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MenuBookIcon />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                            Customer Biometric Device Integration Guide & Troubleshooting Manual
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Comprehensive setup instructions for HR administrators and office IT managers.
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Hardware Compatibility Notice:
                </Typography>
                <Typography variant="body2">
                    Physical hardware verification requires deploying the Local Connector Agent on your local office network.
                    Adapters in this system declare explicit verification statuses: <code>SIMULATION_TESTED</code>, <code>IMPLEMENTED</code>, or <code>NOT_IMPLEMENTED</code>.
                    Do not assume all models from a manufacturer share identical protocols.
                </Typography>
            </Alert>

            {/* Section A: Before You Begin */}
            <Accordion defaultExpanded sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section A: Before You Begin (Prerequisites)
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        Ensure you have the following information and permissions before configuring your terminal:
                    </Typography>
                    <ul>
                        <li><strong>Device Manufacturer & Model Number:</strong> Check the serial sticker on the back of the device (e.g. ZKTeco SilkBio-100TC, eSSL K30 Pro, Hikvision DS-K1T671).</li>
                        <li><strong>Static Local IP Address:</strong> Ensure the device is assigned a reserved IP address on your office router or DHCP server.</li>
                        <li><strong>Device Communication Port:</strong> Default for ZKTeco/eSSL is TCP 4370; Hikvision uses HTTPS port 443; Matrix uses port 80/443.</li>
                        <li><strong>Communication Key / Password:</strong> If configured on your device (default is usually 0 or blank), you will need it for authentication.</li>
                        <li><strong>HR Portal Privileges:</strong> You must be signed in as a Tenant Administrator or authorized HR Manager with leaves/attendance module rights.</li>
                    </ul>
                </AccordionDetails>
            </Accordion>

            {/* Section B: Device & LAN Network Setup */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section B: Device Setup & Network Configuration
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        How to configure the device on your local office network:
                    </Typography>
                    <ol>
                        <li>On the physical terminal, press the <strong>Menu / M/OK</strong> button to access Administrator Settings.</li>
                        <li>Navigate to <strong>Comm. &gt; Ethernet / Network</strong>.</li>
                        <li>Set <strong>DHCP</strong> to <em>Off</em> and assign a static IP address in your subnet (e.g., <code>192.168.1.201</code>).</li>
                        <li>Set the <strong>Subnet Mask</strong> (e.g., <code>255.255.255.0</code>) and <strong>Gateway</strong>.</li>
                        <li>Under <strong>Comm. &gt; PC Connection</strong>, confirm the <strong>Comm Port</strong> is set to <code>4370</code> and note the <strong>Comm Key</strong>.</li>
                    </ol>
                </AccordionDetails>
            </Accordion>

            {/* Section C: Local Connector Agent */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section C: Local Connector Installation (Customer LAN)
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        The <strong>Local Connector Agent</strong> is the recommended secure connection method:
                    </Typography>
                    <ul>
                        <li><strong>Outbound HTTPS Only:</strong> Initiates outbound calls on Port 443 to the portal. <strong>Zero open inbound ports</strong> on your office firewall.</li>
                        <li><strong>Installation:</strong> Download <code>connector_agent/connector_agent.py</code> onto a PC or local server connected to your office LAN.</li>
                        <li><strong>Token Registration:</strong> In the portal, click <em>Local Connectors &gt; Register Connector</em>. Copy the secret token (starts with <code>bio_conn_</code>).</li>
                        <li><strong>Configuration:</strong> Add the token and portal URL to <code>connector_config.json</code> and start the daemon.</li>
                        <li><strong>Offline Queue:</strong> If your office internet goes down, the connector stores up to 5,000 punches locally and flushes them automatically when connectivity resumes.</li>
                    </ul>
                </AccordionDetails>
            </Accordion>

            {/* Section D: Employee Mapping */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section D: Employee Biometric ID Mapping
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        Matching biometric device user IDs to portal employee records:
                    </Typography>
                    <ul>
                        <li>Biometric hardware registers employees by a numeric or alphanumeric user ID (e.g., <code>101</code>, <code>102</code>).</li>
                        <li>In the portal, navigate to <strong>Employee Mapping</strong> and click <strong>Map Employee</strong>.</li>
                        <li>Select the portal employee (e.g. <em>EMP-00101 John Doe</em>) and enter the corresponding biometric terminal ID (e.g. <code>101</code>).</li>
                        <li><strong>Retroactive Backfill:</strong> If punches arrived before the employee was mapped, saving the mapping will automatically resolve and link all unmapped staged punches for that ID!</li>
                    </ul>
                </AccordionDetails>
            </Accordion>

            {/* Section E: Synchronization & Deduplication */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section E: Synchronization & Duplicate Prevention
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        How synchronization works and how duplicates are prevented:
                    </Typography>
                    <ul>
                        <li><strong>Deterministic Fingerprinting:</strong> Every punch is hashed using SHA-256 over <code>company_id + device_id + user_id + UTC timestamp</code>.</li>
                        <li><strong>Idempotent Retries:</strong> If a network failure occurs during upload and the batch is retried, duplicate punches are automatically skipped without throwing errors or creating duplicate rows.</li>
                        <li><strong>Isolated Staging:</strong> Punches are saved into <code>BiometricPunchStaging</code>. They <strong>never</strong> overwrite existing attendance or payroll without explicit administrative preview.</li>
                    </ul>
                </AccordionDetails>
            </Accordion>

            {/* Section F: Troubleshooting & Error Codes */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section F: Troubleshooting Common Issues
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        Solutions for common operational and network errors:
                    </Typography>
                    <Stack spacing={1.5}>
                        <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                Error: "Device offline or unreachable"
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                                1. Verify the device is powered on and connected to the Ethernet switch.<br />
                                2. Ping the device IP from the machine running the connector agent: <code>ping 192.168.1.201</code>.<br />
                                3. Check for IP address conflict with another office printer or PC.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                                Error: "Authentication failed / Invalid communication key"
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                                Verify the Communication Key on the physical terminal under <em>Menu &gt; Comm &gt; PC Connection</em>. Enter the matching key in device settings.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                                Issue: "Device Clock Skew / Timestamps inaccurate"
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#78350f' }}>
                                Terminal clock has drifted from NTP server. Access <em>Menu &gt; System &gt; Date Time</em> and synchronize with Asia/Kolkata standard time.
                            </Typography>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Section G: Security Best Practices */}
            <Accordion sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #eef0f4', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Section G: Security & Data Privacy Guidelines
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
                        Enterprise security guidelines for biometric integration:
                    </Typography>
                    <ul>
                        <li><strong>No Raw Biometric Templates:</strong> The portal only collects timestamp metadata (In/Out punch times). Fingerprint minified templates and facial models remain on the physical device and are never uploaded to the cloud.</li>
                        <li><strong>Credential Protection:</strong> All device passwords and communication keys stored in the database are encrypted at rest using AES-256.</li>
                        <li><strong>Connector Revocation:</strong> If a local connector machine is decommissioned, immediately revoke its token from the portal dashboard.</li>
                    </ul>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default BiometricIntegrationGuide;

